import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../context/AuthContext";
import { useAlert } from "../components/AlertModal";
import {
  fetchShiftStaffs,
  fetchShiftRequestOffs,
  createShiftRequestOff,
  deleteShiftRequestOff,
} from "../api/shiftApi";
import "../styles/ShiftRequestOffPage.css";

const formatMonthValue = (year, month) => {
  return `${year}-${String(month).padStart(2, "0")}`;
};

const getTodayMonthValue = () => {
  const now = new Date();
  return formatMonthValue(now.getFullYear(), now.getMonth() + 1);
};

const parseMonthValue = (value) => {
  const [year, month] = value.split("-").map(Number);
  return { year, month };
};

const getWeekdayLabel = (dateStr) => {
  const d = new Date(dateStr);
  return ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
};

const isWeekend = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay();
  return day === 0 || day === 6;
};

const buildMonthDates = (year, month) => {
  const lastDay = new Date(year, month, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => {
    const day = i + 1;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  });
};

function ShiftRequestOffPage() {
  const { user: userInfo, loading } = useAuthContext();
  const { showAlert } = useAlert();

  const [monthValue, setMonthValue] = useState(getTodayMonthValue());
  const [staffs, setStaffs] = useState([]);
  const [offRequests, setOffRequests] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [savingKey, setSavingKey] = useState("");

  const { year, month } = useMemo(() => parseMonthValue(monthValue), [monthValue]);
  const dates = useMemo(() => buildMonthDates(year, month), [year, month]);

  const loadData = useCallback(async () => {
    if (!userInfo?.store_id) return;

    setLoadingPage(true);
    try {
      const [staffData, offData] = await Promise.all([
        fetchShiftStaffs({ storeId: userInfo.store_id, isActive: true }),
        fetchShiftRequestOffs({ storeId: userInfo.store_id, year, month }),
      ]);

      setStaffs(Array.isArray(staffData) ? staffData : staffData.results || []);
      setOffRequests(Array.isArray(offData) ? offData : offData.results || []);
    } catch (error) {
      console.error("希望休データ取得エラー:", error);
      showAlert("希望休データの取得に失敗しました。");
    } finally {
      setLoadingPage(false);
    }
  }, [userInfo?.store_id, year, month, showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const offRequestMap = useMemo(() => {
    const map = {};
    for (const item of offRequests) {
      map[`${item.staff}_${item.target_date}`] = item;
    }
    return map;
  }, [offRequests]);

  const handleToggleOffRequest = async (staffId, targetDate) => {
    const key = `${staffId}_${targetDate}`;
    const existing = offRequestMap[key];

    setSavingKey(key);
    try {
      if (existing) {
        await deleteShiftRequestOff(existing.id);
        setOffRequests((prev) => prev.filter((item) => item.id !== existing.id));
      } else {
        const created = await createShiftRequestOff({
          store: userInfo.store_id,
          staff: staffId,
          target_date: targetDate,
          note: "",
        });
        setOffRequests((prev) => [...prev, created]);
      }
    } catch (error) {
      console.error("希望休更新エラー:", error);
      const detail =
        error?.details?.detail ||
        error?.details?.error ||
        "希望休の更新に失敗しました。";
      showAlert(detail);
    } finally {
      setSavingKey("");
    }
  };

  if (loading || !userInfo) {
    return <div>読み込み中...</div>;
  }

  return (
    <div className="shift-off-page">
      <div className="shift-off-header">
        <div>
          <h2 className="shift-off-title">希望休入力</h2>
          <div className="shift-off-subtitle">
            セルをクリックすると希望休の登録/解除ができます
          </div>
        </div>

        <div className="shift-off-controls">
          <input
            type="month"
            className="shift-off-month-input"
            value={monthValue}
            onChange={(e) => setMonthValue(e.target.value)}
          />
          <button
            type="button"
            className="shift-off-btn"
            onClick={loadData}
            disabled={loadingPage}
          >
            {loadingPage ? "読込中..." : "再読込"}
          </button>
        </div>
      </div>

      {loadingPage ? (
        <div className="shift-off-loading">読み込み中...</div>
      ) : (
        <div className="shift-off-table-wrap">
          <table className="shift-off-table">
            <thead>
              <tr>
                <th className="shift-off-staff-col">スタッフ</th>
                {dates.map((dateStr) => (
                  <th
                    key={dateStr}
                    className={`shift-off-date-col ${isWeekend(dateStr) ? "is-weekend" : ""}`}
                  >
                    <div>{Number(dateStr.slice(-2))}</div>
                    <div className="shift-off-weekday">{getWeekdayLabel(dateStr)}</div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {staffs.map((staff) => (
                <tr key={staff.id}>
                  <td className="shift-off-staff-cell">{staff.name}</td>

                  {dates.map((dateStr) => {
                    const key = `${staff.id}_${dateStr}`;
                    const active = !!offRequestMap[key];
                    const saving = savingKey === key;

                    return (
                      <td key={key} className="shift-off-cell">
                        <button
                          type="button"
                          className={`shift-off-cell-btn ${active ? "is-active" : ""}`}
                          onClick={() => handleToggleOffRequest(staff.id, dateStr)}
                          disabled={saving}
                          title={active ? "希望休を解除" : "希望休を登録"}
                        >
                          {saving ? "..." : active ? "休" : ""}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ShiftRequestOffPage;